import { useEffect, useState } from "react";
import { api } from "../lib/api";
import Layout from "../components/Layout";
import { toast } from "sonner";
import { RefreshCw, CheckCircle2, XCircle, PauseCircle, Play } from "lucide-react";

const TABS = [["PENDING_APPROVAL","Pending Approval"],["APPROVED","Approved"],["PENDING_EMAIL","Awaiting Email Verify"],["SUSPENDED","Suspended"],["REJECTED","Rejected"]];

export default function AdminEmployers() {
  const [status, setStatus] = useState("PENDING_APPROVAL");
  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setBusy(true);
    try { const { data } = await api.get("/admin/employers", { params: status?{status}:{} }); setRows(data); }
    catch (e) { toast.error(e.response?.data?.detail || "Load failed"); }
    finally { setBusy(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [status]);

  const act = async (uid, action, reason) => {
    try {
      await api.post(`/admin/employers/${uid}/action`, { action, reason });
      toast.success(`Employer ${action.toLowerCase()}d`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Action failed"); }
  };

  const ask = async (uid, action) => {
    const reason = window.prompt(`${action}: reason (optional, shown to employer)`) || "";
    if (reason === null) return;
    await act(uid, action, reason);
  };

  return (
    <Layout>
      <div className="mb-6 flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="text-[11px] font-semibold uppercase tracking-widest text-sky-600 mb-1">Admin</div>
          <h1 className="font-display text-3xl font-bold text-slate-900 tracking-tight">Employer approvals</h1>
          <p className="text-sm text-slate-500 mt-1">Review new employer signups before granting Talent Network access.</p>
        </div>
        <button onClick={load} data-testid="emp-refresh" className="h-9 px-3 rounded-md border border-slate-300 text-sm inline-flex items-center gap-1.5"><RefreshCw size={13}/> Refresh</button>
      </div>

      <div className="border-b border-slate-200 mb-5 flex gap-1 overflow-x-auto">
        {TABS.map(([id,lbl])=>(
          <button key={id} data-testid={`emp-tab-${id}`} onClick={()=>setStatus(id)} className={`px-4 py-2 text-sm font-medium whitespace-nowrap border-b-2 ${status===id?"border-sky-600 text-slate-900":"border-transparent text-slate-500 hover:text-slate-800"}`}>{lbl}</button>
        ))}
      </div>

      <div className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden">
        {rows.length===0 ? <div className="p-10 text-center text-sm text-slate-500">{busy?"Loading…":"No employers in this queue."}</div> : (
          <table className="w-full text-sm">
            <thead className="bg-slate-100/70 text-slate-600 text-[11px] uppercase tracking-widest">
              <tr><th className="text-left px-4 py-3">Contact</th><th className="text-left px-4 py-3">Company</th><th className="text-left px-4 py-3">Industry</th><th className="text-left px-4 py-3">Source</th><th className="text-left px-4 py-3">Signed up</th><th className="text-right px-4 py-3">Actions</th></tr>
            </thead>
            <tbody>{rows.map(r => (
              <tr key={r.id} className="border-t border-slate-100" data-testid={`emp-row-${r.id}`}>
                <td className="px-4 py-3"><div className="font-semibold text-slate-900">{r.name}</div><div className="text-xs text-slate-500">{r.email}</div></td>
                <td className="px-4 py-3">{r.company?.name || "—"}</td>
                <td className="px-4 py-3 text-slate-600">{r.company?.industry || "—"}</td>
                <td className="px-4 py-3"><span className="text-[10px] font-semibold uppercase tracking-widest px-2 py-0.5 rounded border border-slate-200 bg-slate-50 text-slate-700">{r.company?.sales_source || "—"}</span></td>
                <td className="px-4 py-3 text-xs text-slate-500">{r.created_at ? new Date(r.created_at).toLocaleDateString() : "—"}</td>
                <td className="px-4 py-3 text-right space-x-1.5">
                  {status !== "APPROVED" && <button data-testid={`emp-approve-${r.id}`} onClick={()=>act(r.id,"APPROVE")} className="h-8 px-3 text-[11px] rounded border border-emerald-300 bg-emerald-50 text-emerald-800 font-semibold inline-flex items-center gap-1"><CheckCircle2 size={11}/> Approve</button>}
                  {status === "APPROVED" && <button data-testid={`emp-suspend-${r.id}`} onClick={()=>ask(r.id,"SUSPEND")} className="h-8 px-3 text-[11px] rounded border border-amber-300 bg-amber-50 text-amber-800 font-semibold inline-flex items-center gap-1"><PauseCircle size={11}/> Suspend</button>}
                  {status === "SUSPENDED" && <button data-testid={`emp-reinstate-${r.id}`} onClick={()=>act(r.id,"REINSTATE")} className="h-8 px-3 text-[11px] rounded border border-emerald-300 bg-emerald-50 text-emerald-800 font-semibold inline-flex items-center gap-1"><Play size={11}/> Reinstate</button>}
                  {status !== "REJECTED" && status !== "APPROVED" && <button data-testid={`emp-reject-${r.id}`} onClick={()=>ask(r.id,"REJECT")} className="h-8 px-3 text-[11px] rounded border border-rose-300 bg-rose-50 text-rose-700 font-semibold inline-flex items-center gap-1"><XCircle size={11}/> Reject</button>}
                </td>
              </tr>
            ))}</tbody>
          </table>
        )}
      </div>
    </Layout>
  );
}
